/**
 * ContractGateControl — admin opt-in for the blocking merge gate
 * (governance-mvp brief 05).
 *
 * Toggling on flips the per-project `contractGateEnabled` flag; the required
 * `fragments/contract-compliance` status check is then installed best-effort at
 * setup and self-healed on the next CI ingest. The badge reflects enforcement
 * reality (advisory → pending → blocking), and a missing-admin-scope install
 * surfaces manual-add instructions rather than a hard failure.
 */
import * as React from 'react';
import { ShieldCheck } from '@phosphor-icons/react';
import { Badge, Card, Stack, Switch, Text } from '@fragments-sdk/ui';
import { useMutation, useQuery } from 'convex/react';
import { api } from '@/convex';
import type { Id } from '@/convex';
import { useClerkUser } from '@/hooks/useClerkUser';
import { useOrgContext } from '@/hooks/useOrgContext';
import s from './contract-gate-control.module.scss';

type GateState = {
  enabled: boolean;
  installedAt: number | null;
  branch: string | null;
  needsReauth: boolean;
  lastError: string | null;
};

type GateBadge = { label: string; variant: 'success' | 'warning' | 'outline' };

// Enforcement reality, not just the flag: only `blocking` once the required
// check is actually installed. Criticality/state → semantic chroma only.
function gateBadge(state: GateState): GateBadge {
  if (!state.enabled) return { label: 'Advisory', variant: 'outline' };
  if (state.needsReauth) return { label: 'Action needed', variant: 'warning' };
  if (state.installedAt) return { label: 'Blocking', variant: 'success' };
  return { label: 'Pending install', variant: 'outline' };
}

export function ContractGateControl({
  projectId,
}: {
  projectId: Id<'projects'>;
}) {
  const { activeRole } = useOrgContext();
  const { user } = useClerkUser();
  const isAdmin = activeRole === 'owner' || activeRole === 'admin';

  const state = useQuery(api.projects.getContractGateState, { projectId });
  const setEnabled = useMutation(api.projects.setContractGateEnabled);

  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  if (!state) return null;

  const badge = gateBadge(state);

  async function handleToggle(next: boolean) {
    if (!isAdmin || !user?.id) return;
    setPending(true);
    setError(null);
    try {
      await setEnabled({ projectId, clerkUserId: user.id, enabled: next });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update gate');
    } finally {
      setPending(false);
    }
  }

  return (
    <Card as="section" variant="outlined" padding="md" className={s.gateCard}>
      <div className={s.gateHeader}>
        <div className={s.gateIcon}>
          <ShieldCheck size={20} weight="duotone" />
        </div>
        <Stack gap="xs" className={s.gateText}>
          <div className={s.gateTitleRow}>
            <Text as="div" size="sm" weight="semibold">
              Required merge check
            </Text>
            <Badge variant={badge.variant} size="sm">
              {badge.label}
            </Badge>
          </div>
          <Text as="p" size="xs" color="secondary">
            Promote the <code>fragments/contract-compliance</code> check to a
            required status check so contract-breaking PRs cannot merge.
          </Text>
        </Stack>
        <Switch
          checked={state.enabled}
          onCheckedChange={handleToggle}
          disabled={!isAdmin || pending}
          aria-label="Require the contract-compliance check to merge"
          size="sm"
        />
      </div>

      {state.enabled &&
        state.installedAt &&
        state.branch &&
        !state.needsReauth && (
          <Text as="p" size="xs" color="tertiary" className={s.gateNote}>
            Required check installed on <code>{state.branch}</code>.
          </Text>
        )}

      {state.enabled && !state.installedAt && !state.needsReauth && (
        <Text as="p" size="xs" color="tertiary" className={s.gateNote}>
          The required check installs on the next setup or CI run.
        </Text>
      )}

      {state.needsReauth && (
        <div className={s.gateBanner}>
          <Stack gap="xs">
            <Text as="div" size="xs" weight="semibold">
              Couldn’t update branch protection automatically
            </Text>
            <Text as="p" size="xs" color="secondary">
              The GitHub App is missing the “administration” permission. Add{' '}
              <code>fragments/contract-compliance</code> as a required status
              check on your default branch, or re-grant the App admin access and
              re-run setup.
            </Text>
            {state.lastError && (
              <Text as="p" size="xs" color="tertiary">
                GitHub said: {state.lastError}
              </Text>
            )}
          </Stack>
        </div>
      )}

      {!isAdmin && (
        <Text as="p" size="xs" color="tertiary" className={s.gateNote}>
          Only organization admins can change the merge gate.
        </Text>
      )}

      {error && (
        <Text as="p" size="xs" className={s.gateError}>
          {error}
        </Text>
      )}
    </Card>
  );
}
